'use client'

import { TrackerCountdown } from '@/components/santa-tracker/tracker-countdown'
import { useTrackerClock } from '@/components/santa-tracker/use-tracker-clock'
import { christmasMs, seasonFor, type TrackerCopy, type TrackerLocale } from '@/lib/santa-tracker'

/**
 * Cuenta atrás de la home: cuánto falta para el 25 de diciembre en la zona
 * horaria del visitante (#547).
 *
 * Reutiliza el reloj vivo del tracker (`useTrackerClock`: servidor pinta el
 * instante con el que se renderizó el HTML y el cliente lo sustituye por el
 * real al montar) y su lógica canónica de Navidad (`christmasMs`: medianoche
 * local del 25, calculada con el desfase de diciembre del navegador, nunca con
 * la del servidor). Tras Nochebuena apunta a la Navidad del año siguiente,
 * igual que la cuenta atrás del tracker.
 *
 * La copy (heading y etiquetas) llega resuelta desde servidor como props: este
 * componente no importa tablas de contenido, en la línea del ADR-036.
 */

export interface HomeChristmasCountdownProps {
  /** Solo el bloque `countdown` de la copy del tracker (etiquetas + aria). */
  countdown: TrackerCopy['countdown']
  locale: TrackerLocale
  /** Título del bloque: «¿Cuánto falta para Papá Noel?». */
  heading: string
  /** Instante con el que el servidor renderizó el HTML (par de `useTrackerClock`). */
  initialNowMs: number
  /** Desfase con el que calculó el servidor (zona por defecto del sitio). */
  serverUtcOffsetHours: number
}

export function HomeChristmasCountdown({
  countdown,
  locale,
  heading,
  initialNowMs,
  serverUtcOffsetHours,
}: HomeChristmasCountdownProps) {
  const clock = useTrackerClock(initialNowMs, serverUtcOffsetHours)

  // Misma regla que `computeTrackerState`: si la Navidad de este año ya pasó,
  // la cuenta atrás mira a la del año que viene.
  const year = new Date(clock.nowMs).getUTCFullYear()
  const season = seasonFor(year)
  const targetMs = christmasMs(season, clock)
  const nextChristmasMs = christmasMs(seasonFor(season.year + 1), clock)
  const remainingMs = Math.max(
    0,
    (targetMs > clock.nowMs ? targetMs : nextChristmasMs) - clock.nowMs
  )

  return (
    <section className="flex w-full flex-col items-center gap-2 py-1">
      <TrackerCountdown
        countdown={countdown}
        density="compact"
        locale={locale}
        heading={heading}
        remainingMs={remainingMs}
        arriving={false}
      />
    </section>
  )
}
